import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { formatCurrency } from '../../utils/onboardingUtils';
import styles from './IncomeSourcesSection.module.css';

/** Income-source editor. Line items are saved when the user runs a what-if scenario. */

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois',
  'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
  'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota',
  'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

const AMOUNT_RANGE = {
  // Salary continuation at a high professional wage.
  severance: { min: 0, max: 15000, step: 100 },
  // Near the highest US state weekly benefit (~$1,050 x 4.33).
  unemployment: { min: 0, max: 4500, step: 50 },
  // A strong contract or freelance month during a career shift.
  temporary: { min: 0, max: 8000, step: 50 },
  // A couple of rentals, or rent plus dividends.
  passive: { min: 0, max: 8000, step: 50 },
};

const WEEKLY_WAGE_MAX = 100000;

const CATEGORIES = [
  {
    id: 'severance',
    number: '1',
    title: 'Severance Income',
    subtitle: 'Add your employer severance package details.',
    addLabel: 'Add severance line item',
    anotherLabel: 'Add another severance line item',
    icon: 'shield',
  },
  {
    id: 'unemployment',
    number: '2',
    title: 'Unemployment Benefit',
    subtitle: 'Enter your state and wage details to estimate benefits.',
    addLabel: 'Add income line item',
    anotherLabel: 'Add another unemployment benefit line item',
    icon: 'briefcase',
  },
  {
    id: 'temporary',
    number: '3',
    title: 'Temporary Income',
    subtitle: 'Add contract, freelance or other temporary income.',
    addLabel: 'Add income line item',
    anotherLabel: 'Add another temporary income line item',
    icon: 'clock',
  },
  {
    id: 'passive',
    number: '4',
    title: 'Real Estate / Passive Income',
    subtitle: 'Add rental, dividend or other passive income.',
    addLabel: 'Add income line item',
    anotherLabel: 'Add another passive income line item',
    icon: 'house',
  },
];

const ICONS = {
  shield: ShieldIcon,
  briefcase: BriefcaseIcon,
  clock: ClockIcon,
  house: HouseIcon,
};

let lineSequence = 0;

function nextId(prefix) {
  lineSequence += 1;
  return `${prefix}-${Date.now().toString(36)}-${lineSequence}`;
}

function createInitialItems() {
  return {
    severance: [{
      id: 'severance-initial',
      name: 'Employer Package',
      monthlyAmount: 0,
      startDate: '2027-10-01',
      endDate: '2027-12-31',
      payoutType: 'salary',
    }],
    unemployment: [{
      id: 'unemployment-initial',
      state: 'California',
      lastWeeklyWage: 0,
      monthlyAmount: 0,
      startDate: '2027-01-01',
      endDate: '2027-06-30',
    }],
    temporary: [{
      id: 'temporary-initial',
      name: '01 Consulting Contract',
      monthlyAmount: 0,
      startDate: '2027-01-01',
      endDate: '2027-06-30',
    }],
    passive: [{
      id: 'passive-initial',
      name: 'Tenant Rental Payments',
      monthlyAmount: 0,
      startDate: '2027-01-01',
      endDate: '2027-06-30',
    }],
  };
}

function createBlankItem(category) {
  const id = nextId(category);
  if (category === 'unemployment') {
    return {
      id,
      state: 'California',
      lastWeeklyWage: 0,
      monthlyAmount: 0,
      startDate: '',
      endDate: '',
    };
  }

  return {
    id,
    name: '',
    monthlyAmount: 0,
    startDate: '',
    endDate: '',
    payoutType: 'salary',
  };
}

const INCOME_CATEGORY_IDS = ['severance', 'unemployment', 'temporary', 'passive'];
const INCOME_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const INCOME_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function asIncomeMoney(value, max) {
  const number = Math.round(Number(value) || 0);
  if (!Number.isFinite(number)) return 0;
  return Math.min(max, Math.max(0, number));
}

function asIncomeDate(value) {
  const text = String(value || '');
  return INCOME_DATE_PATTERN.test(text) ? text : '';
}

function asIncomeId(value, fallback) {
  const text = String(value || '');
  return INCOME_ID_PATTERN.test(text) ? text : fallback;
}

function normalizeIncomeLine(category, item, index) {
  const id = asIncomeId(item?.id, `${category}-${index + 1}`);
  const monthlyAmount = asIncomeMoney(item?.monthlyAmount, AMOUNT_RANGE[category].max);
  const startDate = asIncomeDate(item?.startDate);
  const endDate = asIncomeDate(item?.endDate);
  if (category === 'unemployment') {
    const state = US_STATES.includes(item?.state) ? item.state : 'California';
    return {
      id,
      state,
      lastWeeklyWage: asIncomeMoney(item?.lastWeeklyWage, WEEKLY_WAGE_MAX),
      monthlyAmount,
      startDate,
      endDate,
    };
  }
  const line = {
    id,
    name: String(item?.name || '').trim().slice(0, 80),
    monthlyAmount,
    startDate,
    endDate,
  };
  if (category === 'severance') {
    line.payoutType = item?.payoutType === 'lump' ? 'lump' : 'salary';
  }
  return line;
}

/** Saved payload shape. Returns null when nothing valid has been stored yet. */
export function normalizeIncomeSources(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const hasCategory = INCOME_CATEGORY_IDS.some((id) => Array.isArray(raw[id]));
  if (!hasCategory) return null;
  return INCOME_CATEGORY_IDS.reduce((next, category) => {
    const lines = Array.isArray(raw[category]) ? raw[category] : [];
    next[category] = lines
      .filter((item) => item && typeof item === 'object')
      .map((item, index) => normalizeIncomeLine(category, item, index));
    return next;
  }, {});
}

export function resolveIncomeSources(raw) {
  const normalized = normalizeIncomeSources(raw);
  if (!normalized) return createInitialItems();
  const defaults = createInitialItems();
  return INCOME_CATEGORY_IDS.reduce((next, category) => {
    const lines = normalized[category];
    next[category] = lines.length > 0 ? lines : defaults[category];
    return next;
  }, {});
}

/** Drop line items the user left at $0 so those categories stay unset in the database. */
export function incomeSourcesForSave(raw) {
  const normalized = normalizeIncomeSources(raw) || createInitialItems();
  return INCOME_CATEGORY_IDS.reduce((next, category) => {
    next[category] = (normalized[category] || []).filter((line) => line.monthlyAmount > 0);
    return next;
  }, {});
}

export function incomeSourcesEqual(left, right) {
  const normalizedLeft = normalizeIncomeSources(left);
  const normalizedRight = normalizeIncomeSources(right);
  if (!normalizedLeft || !normalizedRight) return false;
  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function parseAmount(raw) {
  const digits = String(raw).replace(/[^\d]/g, '');
  if (!digits) return 0;
  return Number(digits);
}

const amountFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function formatAmount(value) {
  return amountFormatter.format(Math.max(0, Math.round(Number(value) || 0)));
}

function fillPercent(value, min, max) {
  if (max <= min) return 0;
  return ((clamp(value, min, max) - min) / (max - min)) * 100;
}

function lineTitle(category, index, count) {
  if (category === 'severance' || count > 1) return `Line Item ${index + 1}`;
  return 'Line Item';
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 1.6 21 5.1v6.6c0 5-3.6 8.4-9 10.4-5.4-2-9-5.4-9-10.4V5.1L12 1.6Z" fill="#047857" />
      <path d="M12 3.5 18.8 6.2v5.1c0 3.7-2.7 6.3-6.8 7.8-4.1-1.5-6.8-4.1-6.8-7.8V6.2L12 3.5Z" fill="#34d399" />
      <path d="m8 11.7 2.5 2.5 5.4-5.6" fill="none" stroke="#ffffff" strokeWidth="2.15" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8.6 7.4V6A1.7 1.7 0 0 1 10.3 4.3h3.4A1.7 1.7 0 0 1 15.4 6v1.4" fill="none" stroke="#f59e0b" strokeWidth="1.9" strokeLinecap="round" />
      <rect x="2.5" y="7.3" width="19" height="12.5" rx="2.4" fill="#0369a1" />
      <path d="M2.5 12h19" fill="none" stroke="#38bdf8" strokeWidth="1.9" />
      <rect x="10.2" y="10.7" width="3.6" height="2.7" rx="0.55" fill="#fbbf24" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9.2" fill="#d97706" />
      <circle cx="12" cy="12" r="7.15" fill="#fff7ed" />
      <path d="M12 6.7V12l3.6 2.15" fill="none" stroke="#c2410c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="1.25" fill="#ea580c" />
    </svg>
  );
}

function HouseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.1 11.4h11.8v8a1 1 0 0 1-1 1H7.1a1 1 0 0 1-1-1v-8Z" fill="#0284c7" />
      <path d="M2.7 12.4 12 3.4l9.3 9-1.6 1.55L12 6.4 4.3 13.95 2.7 12.4Z" fill="#f97316" />
      <rect x="10.25" y="14.1" width="3.5" height="6.3" fill="#fff7ed" />
      <rect x="7.35" y="13.15" width="2.15" height="1.9" rx="0.25" fill="#fef3c7" />
      <rect x="14.5" y="13.15" width="2.15" height="1.9" rx="0.25" fill="#fef3c7" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={open ? styles.chevronOpen : undefined}>
      <path d="m5.6 9 6.4 6.4L18.4 9" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.1" y="4.7" width="17.8" height="15.6" rx="2.5" fill="#fff7ed" />
      <path d="M3.1 7.2a2.5 2.5 0 0 1 2.5-2.5h12.8a2.5 2.5 0 0 1 2.5 2.5V9.4H3.1V7.2Z" fill="#f97316" />
      <rect x="7.2" y="2.8" width="1.7" height="3.5" rx="0.7" fill="#c2410c" />
      <rect x="15.1" y="2.8" width="1.7" height="3.5" rx="0.7" fill="#c2410c" />
      <circle cx="8" cy="12.7" r="1.05" fill="#fb923c" />
      <circle cx="12" cy="12.7" r="1.05" fill="#fdba74" />
      <circle cx="16" cy="12.7" r="1.05" fill="#fdba74" />
      <circle cx="8" cy="16.3" r="1.05" fill="#fdba74" />
      <circle cx="12" cy="16.3" r="1.05" fill="#f97316" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="#0369a1" />
      <circle cx="12" cy="12" r="7.15" fill="#38bdf8" />
      <path d="M12 10.5v5.3" fill="none" stroke="#ffffff" strokeWidth="2.05" strokeLinecap="round" />
      <circle cx="12" cy="7.6" r="1.15" fill="#ffffff" />
    </svg>
  );
}

function BulbIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2.2v1.45M6.15 4.85l1.05 1.05M17.85 4.85l-1.05 1.05M4.3 10.4h1.5M18.2 10.4h1.5" fill="none" stroke="#f59e0b" strokeWidth="1.55" strokeLinecap="round" />
      <path d="M12 4.2a5.15 5.15 0 0 0-2.9 9.4c.4.32.65.78.65 1.28h4.5c0-.5.25-.96.65-1.28A5.15 5.15 0 0 0 12 4.2Z" fill="#fbbf24" />
      <path d="M9.6 15.5h4.8v1.15c0 .7-.55 1.25-1.25 1.25h-2.3c-.7 0-1.25-.55-1.25-1.25V15.5Z" fill="#f59e0b" />
      <path d="M10.1 19.2h3.8" fill="none" stroke="#d97706" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M12 7.1v2.3M9.7 9.6l1.5 1.2" fill="none" stroke="#fffbeb" strokeWidth="1.45" strokeLinecap="round" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="#0f766e" />
      <circle cx="12" cy="12" r="7.15" fill="#2dd4bf" />
      <path d="M9.35 9.7a2.75 2.75 0 1 1 4.55 2.05c-.65.5-1.05.95-1.05 1.85" fill="none" stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round" />
      <circle cx="12.85" cy="16.35" r="1.05" fill="#ffffff" />
    </svg>
  );
}

function TextField({
  id: idProp,
  label,
  value,
  onChange,
  placeholder,
  hideLabel = false,
}) {
  const generatedId = useId();
  const id = idProp || generatedId;

  return (
    <div className={styles.field}>
      {hideLabel ? (
        <span className={styles.labelHidden} aria-hidden="true">Monthly Amount</span>
      ) : (
        <label className={styles.label} htmlFor={id}>{label}</label>
      )}
      <input
        id={id}
        className={styles.control}
        type="text"
        value={value}
        placeholder={placeholder}
        aria-label={hideLabel ? label : undefined}
        autoComplete="off"
        maxLength={80}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function MoneyField({ label, value, max, onChange }) {
  const id = useId();

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>{label}</label>
      <div className={styles.moneyBox}>
        <span className={styles.moneyPrefix} aria-hidden="true">$</span>
        <input
          id={id}
          className={styles.moneyInput}
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          value={formatAmount(value)}
          onChange={(event) => onChange(clamp(parseAmount(event.target.value), 0, max))}
        />
      </div>
    </div>
  );
}

function MoneySlider({ label, value, min, max, step, onChange }) {
  const inputId = useId();
  const sliderId = useId();
  const next = clamp(Number(value) || 0, min, max);

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={inputId}>{label}</label>
      <div className={styles.moneySlider}>
        <div className={styles.moneyBox}>
          <span className={styles.moneyPrefix} aria-hidden="true">$</span>
          <input
            id={inputId}
            className={styles.moneyInput}
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            value={formatAmount(next)}
            onChange={(event) => onChange(clamp(parseAmount(event.target.value), min, max))}
          />
        </div>
        <div className={styles.sliderCol}>
          <input
            id={sliderId}
            className={styles.range}
            type="range"
            min={min}
            max={max}
            step={step}
            value={next}
            aria-label={`${label} slider`}
            style={{ '--fill-pct': `${fillPercent(next, min, max)}%` }}
            onChange={(event) => onChange(clamp(Number(event.target.value) || 0, min, max))}
          />
          <div className={styles.ticks} aria-hidden="true">
            <span>{formatCurrency(min)}</span>
            <span>{formatCurrency(max)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DateField({ label, value, onChange }) {
  const id = useId();

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>{label}</label>
      <div className={styles.dateControl}>
        <span className={styles.dateIcon} aria-hidden="true">
          <CalendarIcon />
        </span>
        <input
          id={id}
          className={styles.control}
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </div>
  );
}

function SelectField({ id: idProp, label, value, options, onChange }) {
  const generatedId = useId();
  const id = idProp || generatedId;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>{label}</label>
      <div className={styles.selectControl}>
        <select
          id={id}
          className={styles.control}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <span className={styles.selectChevron} aria-hidden="true">
          <ChevronIcon open={false} />
        </span>
      </div>
    </div>
  );
}

function PayoutField({ name, value, onChange }) {
  return (
    <fieldset className={`${styles.field} ${styles.payout}`}>
      <legend className={styles.label}>Payout Type</legend>
      <div className={styles.radios}>
        <label className={styles.radio}>
          <input
            type="radio"
            name={name}
            checked={value === 'salary'}
            onChange={() => onChange('salary')}
          />
          <span className={styles.radioMark} aria-hidden="true" />
          Salary continuation
        </label>
        <label className={styles.radio}>
          <input
            type="radio"
            name={name}
            checked={value === 'lump'}
            onChange={() => onChange('lump')}
          />
          <span className={styles.radioMark} aria-hidden="true" />
          Lump sum
        </label>
      </div>
    </fieldset>
  );
}

function SeveranceFields({ item, onChange }) {
  const range = AMOUNT_RANGE.severance;
  const isLumpSum = item.payoutType === 'lump';

  return (
    <div className={styles.stack}>
      <div className={styles.pair}>
        <TextField
          id={`income-focus-${item.id}`}
          label="Employer Package"
          value={item.name}
          placeholder="Employer package"
          onChange={(name) => onChange({ name })}
        />
        <MoneySlider
          label="Monthly Amount"
          value={item.monthlyAmount}
          min={range.min}
          max={range.max}
          step={range.step}
          onChange={(monthlyAmount) => onChange({ monthlyAmount })}
        />
      </div>
      <div className={isLumpSum ? `${styles.datesWithPayout} ${styles.datesLump}` : styles.datesWithPayout}>
        <DateField
          label="Start Date"
          value={item.startDate}
          onChange={(startDate) => onChange({ startDate })}
        />
        {isLumpSum ? null : (
          <DateField
            label="End Date"
            value={item.endDate}
            onChange={(endDate) => onChange({ endDate })}
          />
        )}
        <PayoutField
          name={`payout-${item.id}`}
          value={item.payoutType}
          onChange={(payoutType) => onChange({ payoutType })}
        />
      </div>
    </div>
  );
}

function UnemploymentFields({ item, onChange }) {
  const range = AMOUNT_RANGE.unemployment;

  return (
    <div className={styles.stack}>
      <div className={styles.pair}>
        <SelectField
          id={`income-focus-${item.id}`}
          label="State / Base Wage"
          value={item.state}
          options={US_STATES}
          onChange={(state) => onChange({ state })}
        />
        <MoneyField
          label="Last Weekly Wage"
          value={item.lastWeeklyWage}
          max={WEEKLY_WAGE_MAX}
          onChange={(lastWeeklyWage) => onChange({ lastWeeklyWage })}
        />
      </div>
      <MoneySlider
        label="Monthly Amount"
        value={item.monthlyAmount}
        min={range.min}
        max={range.max}
        step={range.step}
        onChange={(monthlyAmount) => onChange({ monthlyAmount })}
      />
      <div className={styles.dates}>
        <DateField
          label="Start Date"
          value={item.startDate}
          onChange={(startDate) => onChange({ startDate })}
        />
        <DateField
          label="End Date"
          value={item.endDate}
          onChange={(endDate) => onChange({ endDate })}
        />
      </div>
    </div>
  );
}

function NamedIncomeFields({
  item,
  onChange,
  range,
  nameLabel,
  namePlaceholder,
}) {
  return (
    <div className={styles.stack}>
      <div className={styles.pair}>
        <TextField
          id={`income-focus-${item.id}`}
          label={nameLabel}
          hideLabel
          value={item.name}
          placeholder={namePlaceholder}
          onChange={(name) => onChange({ name })}
        />
        <MoneySlider
          label="Monthly Amount"
          value={item.monthlyAmount}
          min={range.min}
          max={range.max}
          step={range.step}
          onChange={(monthlyAmount) => onChange({ monthlyAmount })}
        />
      </div>
      <div className={styles.dates}>
        <DateField
          label="Start Date"
          value={item.startDate}
          onChange={(startDate) => onChange({ startDate })}
        />
        <DateField
          label="End Date"
          value={item.endDate}
          onChange={(endDate) => onChange({ endDate })}
        />
      </div>
    </div>
  );
}

function LineFields({ category, item, onChange }) {
  if (category === 'severance') {
    return <SeveranceFields item={item} onChange={onChange} />;
  }
  if (category === 'unemployment') {
    return <UnemploymentFields item={item} onChange={onChange} />;
  }
  if (category === 'temporary') {
    return (
      <NamedIncomeFields
        item={item}
        onChange={onChange}
        range={AMOUNT_RANGE.temporary}
        nameLabel="Income name"
        namePlaceholder="Contract or income name"
      />
    );
  }
  return (
    <NamedIncomeFields
      item={item}
      onChange={onChange}
      range={AMOUNT_RANGE.passive}
      nameLabel="Income source"
      namePlaceholder="Rental, dividend, or other income"
    />
  );
}

const INCOME_CAROUSEL_QUERY = '(max-width: 1024px)';

function IncomeCarouselArrow({ direction }) {
  const points = direction === 'left' ? 'M19 12H5M11 6l-6 6 6 6' : 'M5 12h14M13 6l6 6-6 6';
  return (
    <svg className={styles.swapIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function useIncomeCarousel(slideCount) {
  const scrollerRef = useRef(null);
  const indexRef = useRef(0);
  const programmaticRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isCarousel, setIsCarousel] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(INCOME_CAROUSEL_QUERY).matches,
  );

  const syncIndex = useCallback((next) => {
    const maxIndex = Math.max(slideCount - 1, 0);
    const clamped = Math.max(0, Math.min(maxIndex, next));
    indexRef.current = clamped;
    setActiveIndex((current) => (current === clamped ? current : clamped));
  }, [slideCount]);

  const readClosest = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const origin = el.getBoundingClientRect().left;
    let closest = 0;
    let min = Infinity;
    [...el.children].forEach((slide, index) => {
      const delta = Math.abs(slide.getBoundingClientRect().left - origin);
      if (delta < min) {
        min = delta;
        closest = index;
      }
    });
    return closest;
  }, []);

  useEffect(() => {
    const media = window.matchMedia(INCOME_CAROUSEL_QUERY);
    const el = scrollerRef.current;
    const applyMode = () => {
      setIsCarousel(media.matches);
      if (media.matches) syncIndex(readClosest());
    };
    const syncFromScroll = () => {
      if (!media.matches) return;
      if (programmaticRef.current) {
        syncIndex(indexRef.current);
        return;
      }
      syncIndex(readClosest());
    };
    const finishScroll = () => {
      programmaticRef.current = false;
      if (media.matches) syncIndex(readClosest());
    };
    applyMode();
    media.addEventListener('change', applyMode);
    el?.addEventListener('scroll', syncFromScroll, { passive: true });
    el?.addEventListener('scrollend', finishScroll);
    window.addEventListener('resize', applyMode);
    return () => {
      media.removeEventListener('change', applyMode);
      el?.removeEventListener('scroll', syncFromScroll);
      el?.removeEventListener('scrollend', finishScroll);
      window.removeEventListener('resize', applyMode);
    };
  }, [readClosest, syncIndex]);

  const goTo = useCallback((next) => {
    const el = scrollerRef.current;
    if (!el || !window.matchMedia(INCOME_CAROUSEL_QUERY).matches) return;
    const maxIndex = Math.max(slideCount - 1, 0);
    const clamped = Math.max(0, Math.min(maxIndex, next));
    const slide = el.children[clamped];
    if (!slide) return;
    programmaticRef.current = true;
    syncIndex(clamped);
    const left = slide.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    window.setTimeout(() => {
      if (!programmaticRef.current) return;
      programmaticRef.current = false;
      syncIndex(indexRef.current);
    }, 520);
  }, [slideCount, syncIndex]);

  return { scrollerRef, activeIndex, isCarousel, goTo, indexRef };
}

function IncomeCard({
  category,
  items,
  isOpen,
  onToggle,
  onAdd,
  onRemove,
  onPatch,
  isSlide = false,
  slideIndex = 0,
  slideCount = 0,
}) {
  const Icon = ICONS[category.icon];
  const panelId = `${category.id}-panel`;
  const titleId = `${category.id}-title`;
  const showAnother = Boolean(category.anotherLabel) && isOpen && items.length > 0;

  return (
    <article
      className={styles.card}
      aria-labelledby={isSlide ? undefined : titleId}
      aria-label={isSlide ? `${category.title}, ${slideIndex + 1} of ${slideCount}` : undefined}
      role={isSlide ? 'group' : undefined}
      aria-roledescription={isSlide ? 'slide' : undefined}
    >
      <div className={styles.cardHead}>
        <button
          type="button"
          className={styles.cardToggle}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={() => onToggle(category.id)}
        >
          <span className={styles.badge}>{category.number}</span>
          <span className={`${styles.cardIcon} ${styles[`cardIcon_${category.id}`]}`} aria-hidden="true">
            <Icon />
          </span>
          <span className={styles.cardCopy}>
            <span id={titleId} className={styles.cardTitle}>{category.title}</span>
            <span className={styles.cardSubtitle}>{category.subtitle}</span>
          </span>
        </button>
        <div className={styles.cardActions}>
          <button type="button" className={styles.addButton} onClick={() => onAdd(category.id)}>
            <PlusIcon />
            {category.addLabel}
          </button>
          <button
            type="button"
            className={styles.chevronButton}
            aria-expanded={isOpen}
            aria-controls={panelId}
            aria-label={isOpen ? `Collapse ${category.title}` : `Expand ${category.title}`}
            onClick={() => onToggle(category.id)}
          >
            <ChevronIcon open={isOpen} />
          </button>
        </div>
      </div>

      {isOpen ? (
        <div id={panelId} className={styles.cardBody}>
          <div className={styles.cardMain}>
            {items.length === 0 ? (
              <p className={styles.empty}>No line items yet.</p>
            ) : (
              <div className={styles.lines}>
                {items.map((item, index) => (
                  <div key={item.id} className={styles.lineItem}>
                    <div className={styles.lineHead}>
                      <p className={styles.lineLabel}>{lineTitle(category.id, index, items.length)}</p>
                      <button
                        type="button"
                        className={styles.trashButton}
                        aria-label={`Remove ${lineTitle(category.id, index, items.length)}`}
                        onClick={() => onRemove(category.id, item.id)}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                    <LineFields
                      category={category.id}
                      item={item}
                      onChange={(patch) => onPatch(category.id, item.id, patch)}
                    />
                  </div>
                ))}
              </div>
            )}

            {category.id === 'unemployment' && items.length > 0 ? (
              <p className={styles.note}>
                <InfoIcon />
                Estimated benefits, state rules and severance offsets may affect timing.
              </p>
            ) : null}
          </div>

          {showAnother ? (
            <button type="button" className={styles.anotherButton} onClick={() => onAdd(category.id)}>
              <PlusIcon />
              {category.anotherLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default function IncomeSourcesSection({
  afterGrid = null,
  onNeedHelp = null,
  items: itemsProp = null,
  onItemsChange = null,
}) {
  const [open, setOpen] = useState({
    severance: true,
    unemployment: true,
    temporary: true,
    passive: true,
  });
  const [uncontrolledItems, setUncontrolledItems] = useState(createInitialItems);
  const items = itemsProp || uncontrolledItems;
  const setItems = useCallback((updater) => {
    if (typeof onItemsChange === 'function') {
      onItemsChange(updater);
      return;
    }
    setUncontrolledItems(updater);
  }, [onItemsChange]);
  const pendingFocusId = useRef(null);
  const {
    scrollerRef,
    activeIndex,
    isCarousel,
    goTo,
    indexRef,
  } = useIncomeCarousel(CATEGORIES.length);
  const showCarouselControls = isCarousel && CATEGORIES.length > 1;

  useEffect(() => {
    const id = pendingFocusId.current;
    if (!id) return;
    pendingFocusId.current = null;
    document.getElementById(`income-focus-${id}`)?.focus();
  }, [items, open]);

  const toggle = (category) => {
    setOpen((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const addItem = (category) => {
    const item = createBlankItem(category);
    pendingFocusId.current = item.id;
    setItems((prev) => ({ ...prev, [category]: [...prev[category], item] }));
    setOpen((prev) => ({ ...prev, [category]: true }));
  };

  const removeItem = (category, id) => {
    setItems((prev) => ({
      ...prev,
      [category]: prev[category].filter((item) => item.id !== id),
    }));
  };

  const patchItem = (category, id, patch) => {
    setItems((prev) => ({
      ...prev,
      [category]: prev[category].map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  };

  return (
    <section className={styles.section} aria-label="Income sources">
      <div
        className={styles.grid}
        ref={scrollerRef}
        tabIndex={showCarouselControls ? 0 : undefined}
        role={showCarouselControls ? 'region' : undefined}
        aria-roledescription={showCarouselControls ? 'carousel' : undefined}
        aria-label={showCarouselControls ? 'Income sources' : undefined}
        onKeyDown={showCarouselControls ? (event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(indexRef.current + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(indexRef.current - 1);
          }
        } : undefined}
      >
        {CATEGORIES.map((category, index) => (
          <IncomeCard
            key={category.id}
            category={category}
            items={items[category.id]}
            isOpen={open[category.id]}
            onToggle={toggle}
            onAdd={addItem}
            onRemove={removeItem}
            onPatch={patchItem}
            isSlide={showCarouselControls}
            slideIndex={index}
            slideCount={CATEGORIES.length}
          />
        ))}
      </div>

      {showCarouselControls ? (
        <div className={styles.nav}>
          <button
            type="button"
            className={styles.swap}
            aria-label="Previous income source"
            disabled={activeIndex === 0}
            onClick={() => goTo(activeIndex - 1)}
          >
            <IncomeCarouselArrow direction="left" />
          </button>
          <div className={styles.dots}>
            {CATEGORIES.map((category, index) => (
              <button
                key={category.id}
                type="button"
                className={index === activeIndex ? `${styles.dot} ${styles.dotActive}` : styles.dot}
                aria-label={category.title}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => goTo(index)}
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.swap}
            aria-label="Next income source"
            disabled={activeIndex >= CATEGORIES.length - 1}
            onClick={() => goTo(activeIndex + 1)}
          >
            <IncomeCarouselArrow direction="right" />
          </button>
          <p className={styles.position} aria-hidden="true">
            {activeIndex + 1} of {CATEGORIES.length}
          </p>
          <p className={styles.live} aria-live="polite">
            {CATEGORIES[activeIndex].title}, {activeIndex + 1} of {CATEGORIES.length}
          </p>
        </div>
      ) : null}

      {afterGrid}

      <div className={styles.footer}>
        <div className={styles.footerIcon} aria-hidden="true">
          <BulbIcon />
        </div>
        <div className={styles.footerCopy}>
          <p className={styles.footerTitle}>Add your income sources</p>
          <p className={styles.footerText}>
            Click on each section to add details. You can add multiple income lines per category. Your entries will help us estimate your runway and show opportunities to extend it.
          </p>
        </div>
        <button type="button" className={styles.helpButton} onClick={onNeedHelp ?? undefined}>
          <HelpIcon />
          Need help?
        </button>
      </div>
    </section>
  );
}
